'use client'

import { useState } from 'react'
import { ChevronLeft, ChevronRight, Receipt, Info } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useGetCreditUsageQuery } from '@/api/user/organization/credits/get-credit-usage-query'
import { useOrganizationId } from '@/features/organization/use-organization-id'
import { format } from 'date-fns'

const ITEMS_PER_PAGE = 10

const STATUS_DISPLAY: Record<string, { label: string; tooltip: string; className: string }> = {
    PENDING: {
        label: 'Processing',
        tooltip: 'This operation is still being processed. Credits will be finalized once complete.',
        className: 'bg-[#FFFBEB] text-[#D97706] border-[#D97706]/20',
    },
    FAILED: {
        label: 'Refunded',
        tooltip: 'This operation did not complete successfully. The credits have been returned to your balance.',
        className: 'bg-[#F0FDF4] text-[#15803D] border-[#22C55E]/20',
    },
}

export const CreditUsageTable = () => {
    const organizationId = useOrganizationId()
    const [page, setPage] = useState(1)

    const { data, isLoading } = useGetCreditUsageQuery(organizationId, {
        page,
        limit: ITEMS_PER_PAGE,
    })

    const usages = data?.data?.usages ?? []
    const meta = data?.meta
    const totalPages = meta ? Math.ceil(meta.totalCount / meta.limit) : 1

    if (isLoading) {
        return <CreditUsageTableSkeleton />
    }

    if (usages.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center">
                <Receipt className="w-12 h-12 text-[#D1D5DB] mb-4" />
                <p className="text-[14px] text-[#6B7280]">No credit usage yet.</p>
            </div>
        )
    }

    return (
        <TooltipProvider delayDuration={0}>
            <div>
                {/* Table Header */}
                <div className="grid grid-cols-[1fr_140px_100px] gap-4 px-4 py-2.5 text-[13px] font-medium text-[#6B7280] border-b border-[#E5E7EB]">
                    <div>Details</div>
                    <div>Date</div>
                    <div className="text-right">Credits</div>
                </div>

                {/* Table Rows */}
                <div className="divide-y divide-[#F3F4F6]">
                    {usages.map((usage) => (
                        <div
                            key={usage.uuid}
                            className="grid grid-cols-[1fr_140px_100px] gap-4 px-4 py-3.5 items-center hover:bg-[#FAFAFA] transition-colors"
                        >
                            {/* Details */}
                            <div className="flex items-center gap-2 min-w-0">
                                <span className="text-[14px] text-[#171717] truncate">{usage.displayName}</span>
                                {STATUS_DISPLAY[usage.status] && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Badge
                                                variant="outline"
                                                className={`text-[11px] px-2 py-0.5 shrink-0 cursor-help gap-1 ${STATUS_DISPLAY[usage.status].className}`}
                                            >
                                                {STATUS_DISPLAY[usage.status].label}
                                                <Info className="w-3 h-3" />
                                            </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="max-w-[240px]">
                                            <p>{STATUS_DISPLAY[usage.status].tooltip}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                )}
                            </div>

                            {/* Date */}
                            <div className="text-[13px] text-[#6B7280]">
                                {format(new Date(usage.createdAt), 'MMM d, yyyy HH:mm')}
                            </div>

                            {/* Credits */}
                            <div className="text-[14px] font-medium text-right tabular-nums text-[#171717]">
                                -{usage.creditCost.toLocaleString()}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 mt-2">
                        <div className="text-[13px] text-[#6B7280]">
                            Page {page} of {totalPages}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page <= 1}>
                                <ChevronLeft className="w-4 h-4" />
                                Previous
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(page + 1)}
                                disabled={page >= totalPages}
                            >
                                Next
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </TooltipProvider>
    )
}

const CreditUsageTableSkeleton = () => (
    <div>
        <div className="grid grid-cols-[1fr_140px_100px] gap-4 px-4 py-2.5 border-b border-[#E5E7EB]">
            <Skeleton className="h-[16px] w-[60px]" />
            <Skeleton className="h-[16px] w-[40px]" />
            <Skeleton className="h-[16px] w-[50px] ml-auto" />
        </div>
        <div className="divide-y divide-[#F3F4F6]">
            {Array.from({ length: 5 }, (_, i) => (
                <div key={i} className="grid grid-cols-[1fr_140px_100px] gap-4 px-4 py-3.5 items-center">
                    <Skeleton className="h-[16px] w-[200px]" />
                    <Skeleton className="h-[16px] w-[100px]" />
                    <Skeleton className="h-[16px] w-[50px] ml-auto" />
                </div>
            ))}
        </div>
    </div>
)
